import { useEffect, useRef, useState } from 'react';
import katex from 'katex';
import { Node, mergeAttributes, nodeInputRule } from '@tiptap/react';
import { NodeViewWrapper, ReactNodeViewRenderer, type NodeViewProps } from '@tiptap/react';
import { colors } from '@yorukiri/ui/tokens';

declare module '@tiptap/react' {
  interface Commands<ReturnType> {
    math: {
      setInlineMath: (latex?: string) => ReturnType;
      setBlockMath: (latex?: string) => ReturnType;
    };
  }
}

type MathAttrs = { latex: string };

function renderKatex(latex: string, display: boolean): string {
  try {
    return katex.renderToString(latex, {
      displayMode: display,
      throwOnError: false,
      strict: false,
      output: 'html',
    });
  } catch {
    return `<span style="color:${colors.bad}">⚠ ${escapeHtml(latex)}</span>`;
  }
}

function escapeHtml(s: string) {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
}

function MathNodeView({ node, updateAttributes, selected, editor, getPos }: NodeViewProps) {
  const display = node.type.name === 'blockMath';
  const latex = (node.attrs as MathAttrs).latex ?? '';
  const [editing, setEditing] = useState(latex === '');
  const [draft, setDraft] = useState(latex);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  useEffect(() => {
    if (!selected && editing && latex !== '') setEditing(false);
  }, [selected, editing, latex]);

  function commit() {
    updateAttributes({ latex: draft });
    setEditing(false);
    if (draft === '' && typeof getPos === 'function') {
      editor.commands.deleteRange({ from: getPos(), to: getPos() + node.nodeSize });
    }
  }

  function handleKey(e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey && !display) {
      e.preventDefault();
      commit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setDraft(latex);
      setEditing(false);
    }
  }

  if (editing) {
    const common = {
      ref: (el: HTMLInputElement | HTMLTextAreaElement | null) => {
        inputRef.current = el;
      },
      value: draft,
      onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setDraft(e.target.value),
      onBlur: commit,
      onKeyDown: handleKey,
      placeholder: display ? 'e.g. \\int_0^\\infty e^{-x^2}\\,dx' : 'e.g. a^2 + b^2 = c^2',
      style: editorStyle(display),
    } as const;
    return (
      <NodeViewWrapper as={display ? 'div' : 'span'} style={{ display: display ? 'block' : 'inline-block' }}>
        {display ? <textarea {...common} rows={3} /> : <input {...common} />}
      </NodeViewWrapper>
    );
  }

  return (
    <NodeViewWrapper
      as={display ? 'div' : 'span'}
      onClick={() => setEditing(true)}
      contentEditable={false}
      style={{
        display: display ? 'block' : 'inline-block',
        cursor: 'text',
        padding: display ? '8px 12px' : '0 2px',
        borderRadius: 4,
        background: selected ? colors.accentSoft : 'transparent',
        transition: 'background 0.1s',
      }}
      dangerouslySetInnerHTML={{ __html: renderKatex(latex, display) }}
    />
  );
}

function editorStyle(display: boolean): React.CSSProperties {
  return {
    background: colors.surface2,
    border: `1px solid ${colors.line2}`,
    borderRadius: 6,
    padding: display ? '10px 12px' : '2px 8px',
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
    fontSize: display ? 13 : 12.5,
    color: colors.ink,
    outline: 'none',
    width: display ? '100%' : 'auto',
    minWidth: display ? undefined : 80,
    resize: display ? 'vertical' : undefined,
  };
}

export const InlineMath = Node.create({
  name: 'inlineMath',
  inline: true,
  group: 'inline',
  atom: true,
  selectable: true,

  addAttributes() {
    return {
      latex: { default: '' },
    };
  },

  parseHTML() {
    return [{ tag: 'span[data-math="inline"]' }];
  },

  renderHTML({ HTMLAttributes, node }) {
    return [
      'span',
      mergeAttributes(HTMLAttributes, { 'data-math': 'inline', 'data-latex': node.attrs.latex }),
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(MathNodeView);
  },

  addCommands() {
    return {
      setInlineMath:
        (latex = '') =>
        ({ commands }) =>
          commands.insertContent({ type: this.name, attrs: { latex } }),
      setBlockMath:
        (latex = '') =>
        ({ commands }) =>
          commands.insertContent({ type: 'blockMath', attrs: { latex } }),
    };
  },

  addInputRules() {
    return [
      nodeInputRule({
        find: /\$([^$\n]+)\$$/,
        type: this.type,
        getAttributes: (match) => ({ latex: match[1] ?? '' }),
      }),
    ];
  },
});

export const BlockMath = Node.create({
  name: 'blockMath',
  group: 'block',
  atom: true,
  selectable: true,

  addAttributes() {
    return {
      latex: { default: '' },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-math="block"]' }];
  },

  renderHTML({ HTMLAttributes, node }) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, { 'data-math': 'block', 'data-latex': node.attrs.latex }),
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(MathNodeView);
  },

  addInputRules() {
    return [
      nodeInputRule({
        find: /^\$\$([^$]+)\$\$$/,
        type: this.type,
        getAttributes: (match) => ({ latex: match[1] ?? '' }),
      }),
    ];
  },
});
